// The Scope step (docs/job-view-brief.md section 3; the layout is docs/screens/job-view-screens-round4-settled-set.html,
// frame f1). Answers: did every piece of scope the client asked for get an item, and in which build, package or add-on
// does it sit? Left: the job's builds, packages and add-ons as one table (a build is named here, an add-on tied to the
// builds it applies to, a package given the add-ons it rolls in; publishing is only read here, it is set on the Price
// tab), then the scope rows: the client's words, the source, where the row sits as a compact chip, and a dot per build,
// package and add-on (filled: in that build's base; dashed: in that add-on; the package mark: rolled into that package;
// hollow: that build doesn't carry it). What an AI employee suggests for a row sits under it, with Approve and Send back.
// Right: the View with its marks (Rowan's, reviewed here) and the mark audit. An item's membership (base or an add-on) is
// set on the item panel, never here (Spencer, 2026-10-22). Review pass 1 still shows the scope rows (ScopePanel).

import { alternateGaps, unavailableChoices } from '@zerogap/engine';
import { useMemo, useState } from 'react';
import type { Library, Resolution, ResolvedItem, Scalar } from '@zerogap/engine';
import { deleteRow, fetchRows, insertRow, rpc, toast, updateRow, updateRows, useRows, type Row } from '../../lib/api';
import { longDate } from '../../lib/format';
import { scopeItemIds } from '../../lib/check';
import { addAlternates, keepChild, leaveChildOut, swapToAlternates } from '../../lib/alternates';
import { BUILD_PCTS, baseBuild, pricedUnder } from '../../lib/job';
import { useSession } from '../../lib/session';
import { approveSuggestion, approvedWords, boxValue, isScopeKind, sayError, sendBackSuggestion } from '../../lib/suggest';
import { Btn, Edit, Empty, Field, Ln, Modal, Money, Seg, Tag } from '../../ui/kit';
import { AiChangedTag } from '../../ui/unverified';
import { useSignedUrl } from './Files';
import { CatalogPick, RecipePick, ValueEditor, showValue } from './ItemTab';
import { Wide } from '../../ui/wide';
import type { JobCtx } from './Workspace';
import '../../styles/jobview-scope.css';

const show = (v: unknown): string => (v === null || v === undefined || v === '' ? '(blank)' : typeof v === 'object' ? JSON.stringify(v) : String(v));
const ids = (v: unknown): string[] => (Array.isArray(v) ? (v as unknown[]).map(String) : []);

/** Where a scope row points, as text, the way ug.scope_row_points_at reads it: the numbers of its items in order, "the whole job", else its outcome. */
const pointsAt = (row: Row, numberOf: (id: unknown) => string | null) => (row.outcome === 'job' ? 'the whole job' : scopeItemIds(row).length ? scopeItemIds(row).map((id) => numberOf(id) ?? '?').join(', ') : String(row.outcome));

/** True when this resolution prices the item: it is there, it found a recipe, and that recipe is available for it. */
function carries(res: Resolution | null, id: string): boolean {
  if (!res) return false;
  const it = res.items.find((i) => i.id === id);
  if (!it) return false;
  if (it.catalog_item_id && !it.is_custom && !it.recipe_id) return false;
  return !it.recipes?.find((x) => x.recipe_id === it.recipe_id)?.unavailable;
}

/** An add-on's short name, as the drawing's dot columns and chips read it ("Upper cabinet over the folding counter" → "Upper cabinet"). */
export function shortName(name: unknown): string {
  const full = String(name ?? "").trim();
  let head = full.split(/[,(·]| over | under | instead | with | for | in /i)[0]?.trim() || full;
  if (head.length > 18) head = head.split(/\s+/).slice(0, 2).join(" ");
  return head;
}

const STOP = new Set(['the', 'and', 'with', 'for', 'throughout', 'all', 'job', 'inch', 'in.', 'over', 'under', 'per', 'one', 'two']);
const words = (s: string) => s.toLowerCase().split(/[^a-z0-9/]+/).filter((w) => w.length >= 3 && !STOP.has(w));

/** A scope row that is a job standard, read against the job's standards: the standard whose value its words name ("White TFL throughout" → Exterior · TFL white). */
function jobStandardFor(text: string, res: Resolution, lib: Library): string | null {
  const said = new Set(words(text));
  let best: { score: number; words: string } | null = null;
  for (const d of lib.standards) {
    if (d.kind === 'catalog') continue;
    const v = res.standards[d.key];
    if (v === null || v === undefined || v === '') continue;
    const value = d.data_type === 'material' ? lib.materials[String(v)]?.description ?? '' : d.data_type === 'catalog_item' ? lib.catalog[String(v)]?.name ?? '' : showValue(v as Scalar, d.data_type, d.data_type === 'number' ? d.unit ?? null : null);
    const hits = words(value).filter((w) => said.has(w)).length;
    if (hits === 0) continue;
    const score = hits + words(d.label).filter((w) => said.has(w)).length * 0.5;
    if (!best || score > best.score) best = { score, words: `${d.label} · ${value.length > 40 ? `${value.slice(0, 38)}…` : value}` };
  }
  return best?.words ?? null;
}

export function ScopeTab({ ctx }: { ctx: JobCtx }) {
  return (
    <div className="s1">
      <div className="left">
        <OptionsTable ctx={ctx} />
        <ScopePanel ctx={ctx} />
      </div>
      <ViewPane ctx={ctx} />
    </div>
  );
}

/** What the Scope step prices: every build alone, each add-on under the first build it applies to, and the job with every add-on on (for looking items up). */
function usePricings(ctx: JobCtx) {
  const { data, asEntered } = ctx;
  return useMemo(() => {
    const base = baseBuild(data.builds);
    const byBuild = new Map<string, Resolution | null>();
    for (const b of data.builds) byBuild.set(b.id, pricedUnder(asEntered, b, []));
    const appliesTo = (a: Row, b: Row) => ids(a.applies_to).length === 0 || ids(a.applies_to).includes(b.id);
    const byAddOn = new Map<string, { build: Row | null; res: Resolution | null }>();
    for (const a of data.addOns) {
      const build = base && appliesTo(a, base) ? base : data.builds.find((b) => appliesTo(a, b)) ?? base;
      byAddOn.set(a.id, { build, res: pricedUnder(asEntered, build, [a]) });
    }
    const all = data.addOns.length ? pricedUnder(asEntered, base, data.addOns) : asEntered.res;
    const resolved = new Map<string, ResolvedItem>();
    for (const i of [...(asEntered.res?.items ?? []), ...(all?.items ?? [])]) if (!resolved.has(i.id)) resolved.set(i.id, i);
    for (const { res } of byAddOn.values()) for (const i of res?.items ?? []) if (!resolved.has(i.id)) resolved.set(i.id, i);
    return { base, byBuild, byAddOn, resolved, appliesTo };
  }, [asEntered, data.builds, data.addOns]);
}

/** The scope rows of this job: those pointing at nothing, at one of its items, or with a suggestion on this job about them. */
function useScope(ctx: JobCtx) {
  const { data, jobId } = ctx;
  const oppId = data.job!.opportunity_id as string;
  const scope = useRows('scope_row', { eq: { opportunity_id: oppId }, order: ['sort', 'created_at'] });
  const sources = useRows('opportunity_source', { eq: { opportunity_id: oppId }, order: ['received_at'] });
  const suggestions = useRows('suggestion', { eq: { job_id: jobId, status: 'waiting' }, order: ['suggested_at'] });
  const waiting = suggestions.rows.filter((x) => isScopeKind(x.kind));
  const here = (row: Row) => scopeItemIds(row).length === 0 || scopeItemIds(row).some((id) => data.items.some((i) => i.id === id)) || waiting.some((x) => x.target_id === row.id);
  const sourceTitle = (id: unknown) => {
    const src = sources.rows.find((x) => x.id === id);
    return src ? String(src.title || src.kind || 'source') : 'Added by hand';
  };
  return { oppId, scope, sources, waiting, here, sourceTitle };
}

// ---------- builds, packages and add-ons ----------

function OptionsTable({ ctx }: { ctx: JobCtx }) {
  const { data, jobId } = ctx;
  const s = useSession();
  const lib = ctx.r.lib!;
  const labels = s.setting<Record<string, string>>('option_labels', {});
  const buildWord = (labels.build ?? 'Builds').replace(/s$/, '');
  const addOnWord = (labels.add_on ?? 'Add-ons').replace(/s$/, '');
  const canEdit = (s.canEdit('screen.builds') || s.canEdit('screen.jobs')) && !ctx.locked;
  const packagesAll = useRows('package', { eq: { job_id: jobId }, order: ['sort', 'created_at'] });
  // a retired package counts nowhere (2026-10-29): nothing is deleted, Restore (through the connector) brings it back
  const packages = { ...packagesAll, rows: packagesAll.rows.filter((p) => !p.retired_at) };
  const { scope, sourceTitle } = useScope(ctx);
  const p = usePricings(ctx);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<Row | null>(null);
  const [addingOn, setAddingOn] = useState(false);
  const [picking, setPicking] = useState<{ table: 'add_on' | 'package'; row: Row } | null>(null);
  const toggle = (id: string) => setOpen((o) => {
    const n = new Set(o);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });

  const top = data.items.filter((i) => !i.parent_item_id);
  const baseItems = top.filter((i) => !i.add_on_id);
  const held = (a: Row) => top.filter((i) => i.add_on_id === a.id);
  const numberOf = (id: unknown) => data.items.find((i) => i.id === id)?.number ?? '?';
  const buildName = (id: string) => data.builds.find((b) => b.id === id)?.name ?? '…';
  const rowFor = (id: string) => scope.rows.find((x) => scopeItemIds(x).includes(id));

  const makeBase = async (b: Row) => {
    if (!window.confirm(`Make "${b.name}" the base? The proposal leads with it, every tab prices it unless the header picks another, and a locked revision stores it.`)) return;
    try {
      await rpc('set_base_build', { p_build: b.id });
    } catch (e) {
      sayError(e);
    }
  };
  const rename = (table: string, row: Row, value: string) => {
    const v = value.trim();
    if (!v || v === row.name) return;
    void updateRow(table, row.id, { name: v }).catch(sayError);
  };
  const addPackage = async () => {
    const taken = new Set(packages.rows.map((x) => String(x.name).toLowerCase()));
    let name = 'Package A';
    for (const l of 'ABCDEFGHJKLMNPQRSTUVWXYZ') {
      name = `Package ${l}`;
      if (!taken.has(name.toLowerCase())) break;
    }
    try {
      const row = await insertRow('package', { job_id: jobId, name, add_on_ids: [], applies_to: [], published: {}, pricing_overrides: {}, sort: packages.rows.length + 1 });
      setPicking({ table: 'package', row });
    } catch (e) {
      sayError(e);
    }
  };

  /** One item under an open add-on or package: number, name (for L-02 on an alternate), its scope row's words and source, its price. */
  const itemLine = (i: Row, res: Resolution | null) => {
    const r = rowFor(i.id);
    const priced = res?.items.find((x) => x.id === i.id);
    return (
      <div key={i.id}>
        <button type="button" className="no lnk" onClick={() => ctx.go({ tab: 'item', item: i.id, back: 'scope' })}>
          {i.number}
        </button>
        <span>
          {i.name}
          {i.replaces_item_id ? <> <span className="tag addon">for {numberOf(i.replaces_item_id)}</span></> : null}
          <br />
          <span className="w">{r ? `${r.request_text} · ${sourceTitle(r.source_id)}` : 'no scope row points here'}</span>
        </span>
        <span className="r">{priced ? <Money cents={priced.rollup_price_cents} /> : '—'}</span>
      </div>
    );
  };

  const nameBox = (table: string, row: Row) => (
    <input key={`${row.id}:${row.name}`} type="text" className="in" defaultValue={row.name ?? ''} disabled={!canEdit} aria-label="Name" onBlur={(e) => rename(table, row, e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} />
  );
  const chev = (id: string, what: string) => (
    <td className="cv" onClick={() => toggle(id)}>
      <button type="button" className="cvb" aria-expanded={open.has(id)} aria-label={`${open.has(id) ? 'Hide' : 'Show'} ${what}`}>
        <span className={`chev${open.has(id) ? ' open' : ''}`} />
      </button>
    </td>
  );
  // Published came off this table 2026-10-24 (SM1): it is shown and set on the Price tab only; the Kind column went with
  // the row words (F5): the group header says it
  return (
    <>
      <div className="sbar">
        <h4>Builds, packages and add-ons</h4>
        <span className="sp" />
        {canEdit && (
          <>
            <Btn small onClick={() => setEditing({ name: '', global_overrides: {}, is_base: data.builds.length === 0 })}>
              + {buildWord.toLowerCase()}
            </Btn>
            <Btn small onClick={() => void addPackage()}>
              + package
            </Btn>
            <Btn small onClick={() => setAddingOn(true)}>
              + {addOnWord.toLowerCase()}
            </Btn>
          </>
        )}
      </div>
      <div className="tscroll">
        <table className="bo">
          <colgroup>
            <col style={{ width: 28 }} />
            <col style={{ width: '34%' }} />
            <col style={{ width: '28%' }} />
            <col />
            <col style={{ width: 44 }} />
          </colgroup>
          <thead>
            <tr>
              <th></th>
              <th>Name</th>
              <th>Applies to · rolls in</th>
              <th>Items</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr className="kind">
              <td colSpan={5}>Builds · one of them is the base</td>
            </tr>
            {data.builds.length === 0 && (
              <tr>
                <td></td>
                <td colSpan={4} className="only">
                  No builds yet: the job as entered is the only way it is priced.
                </td>
              </tr>
            )}
            {data.builds.map((b) => {
              const res = p.byBuild.get(b.id) ?? null;
              const missing = baseItems.filter((i) => !carries(res, i.id));
              const o = (b.global_overrides ?? {}) as Record<string, Scalar>;
              const pct = (b.pricing_overrides ?? {}) as Record<string, unknown>;
              return [
                <tr key={b.id}>
                  {chev(b.id, `${b.name}'s standards`)}
                  <td className="nm">{nameBox('build', b)}</td>
                  <td className="only">every item, unless an item says otherwise</td>
                  <td className="mono">
                    {baseItems.length - missing.length} of {baseItems.length}
                    {missing.length > 0 && <span className="only"> · not {missing.map((i) => i.number).join(', ')}</span>}
                  </td>
                  <td>{b.is_base ? <span className="tag base">base</span> : null}</td>
                </tr>,
                open.has(b.id) && (
                  <tr key={`${b.id}:open`} className="sub">
                    <td colSpan={5}>
                      <div className="bhome">
                        <span>
                          {Object.keys(o).length === 0 && Object.keys(pct).length === 0
                            ? 'The job’s standards and percentages, nothing changed.'
                            : [
                                ...Object.entries(o).map(([k, v]) => {
                                  const def = lib.standards.find((d) => d.key === k);
                                  return `${def?.label ?? k}: ${def?.data_type === 'material' ? lib.materials[String(v)]?.description ?? '—' : def?.data_type === 'catalog_item' ? lib.catalog[String(v)]?.name ?? '—' : showValue(v, def?.data_type ?? 'text', def?.data_type === 'number' ? def.unit ?? null : null)}`;
                                }),
                                ...BUILD_PCTS.filter((x) => x.key in pct).map((x) => `${x.label} ${String(pct[x.key])}%`),
                              ].join(' · ')}
                          {missing.length > 0 && (
                            <>
                              <br />
                              <span className="w">Doesn't carry: {missing.map((i) => `${i.number} ${i.name}`).join(' · ')}</span>
                            </>
                          )}
                        </span>
                        {canEdit && (
                          <span className="mini">
                            <Btn small onClick={() => setEditing(b)}>
                              Standards and percentages
                            </Btn>
                            {!b.is_base && (
                              <Btn small onClick={() => void makeBase(b)}>
                                Make it the base
                              </Btn>
                            )}
                            {!b.is_base && (
                              <Btn
                                small
                                onClick={async () => {
                                  if (!window.confirm(`Remove the build "${b.name}"?`)) return;
                                  await deleteRow('build', b.id).catch(sayError);
                                }}
                              >
                                Remove
                              </Btn>
                            )}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ),
              ];
            })}

            <tr className="kind">
              <td colSpan={5}>Packages · a set of add-ons rolled into one bid, published per build</td>
            </tr>
            {packages.rows.length === 0 && (
              <tr>
                <td></td>
                <td colSpan={4} className="only">
                  No packages. A package is optional: a set of add-ons priced as one bid.
                </td>
              </tr>
            )}
            {packages.rows.map((pk) => {
              const ons = ids(pk.add_on_ids).map((id) => data.addOns.find((a) => a.id === id)).filter((a): a is Row => !!a);
              const items = ons.flatMap(held);
              const builds = data.builds.filter((b) => ids(pk.applies_to).length === 0 || ids(pk.applies_to).includes(b.id));
              return [
                <tr key={pk.id}>
                  {chev(pk.id, `what ${pk.name} rolls in`)}
                  <td className="nm">{nameBox('package', pk)}</td>
                  <td>
                    <button type="button" className="in pickr" disabled={!canEdit} onClick={() => setPicking({ table: 'package', row: pk })}>
                      <span title={ons.map((a) => a.name).join(' + ')}>{ons.length ? ons.map((a) => shortName(a.name)).join(' + ') : 'no add-ons yet'}</span>
                      <span className="chev" />
                    </button>
                    {ids(pk.applies_to).length > 0 && <span className="only inpk">{builds.map((b) => b.name).join(', ')} only</span>}
                  </td>
                  <td className="mono">
                    {items.length} in {ons.length} {ons.length === 1 ? 'add-on' : 'add-ons'}
                  </td>
                  <td></td>
                </tr>,
                open.has(pk.id) && (
                  <tr key={`${pk.id}:open`} className="sub">
                    <td colSpan={5}>
                      <div className="subl">
                        {ons.length === 0 && <div className="ah"><span className="w">It rolls in no add-on yet: pick them in "Applies to · rolls in".</span></div>}
                        {ons.map((a) => {
                          const res = p.byAddOn.get(a.id)?.res ?? null;
                          return [
                            <div key={`${a.id}:h`} className="ah">
                              <span>
                                {a.name} <span className="tag addon">add-on</span>
                              </span>
                              <span className="w">
                                {held(a).length} {held(a).length === 1 ? 'item' : 'items'} · rolled into this bid, not on the proposal as an add-on
                              </span>
                            </div>,
                            ...held(a).map((i) => itemLine(i, res)),
                          ];
                        })}
                      </div>
                    </td>
                  </tr>
                ),
              ];
            })}

            <tr className="kind">
              <td colSpan={5}>Add-ons · priced on each build they apply to, published on their own</td>
            </tr>
            {data.addOns.length === 0 && (
              <tr>
                <td></td>
                <td colSpan={4} className="only">
                  No add-ons. An option the client may take is an add-on on this job, never a second job.
                </td>
              </tr>
            )}
            {data.addOns.map((a) => {
              const mine = held(a);
              const targets = ids(a.target_items).map(numberOf);
              const inPk = packages.rows.filter((pk) => ids(pk.add_on_ids).includes(a.id));
              const priced = p.byAddOn.get(a.id);
              const prov = mine.some((i) => /provisional/i.test(String(i.note ?? '')));
              const unv = mine.some((i) => i.unverified_since || (i.is_custom && p.resolved.get(i.id)?.unverified.any));
              return [
                <tr key={a.id}>
                  {chev(a.id, `the items in ${a.name}`)}
                  <td className="nm">
                    {nameBox('add_on', a)}
                    {prov && <> <span className="tag fix">provisional</span></>}
                    {unv && <> <span className="tag unv">unverified</span></>}
                  </td>
                  <td>
                    <button type="button" className="in pickr" disabled={!canEdit} onClick={() => setPicking({ table: 'add_on', row: a })}>
                      <span>{ids(a.applies_to).length === 0 ? 'All builds' : `${ids(a.applies_to).map(buildName).join(', ')} only`}</span>
                      <span className="chev" />
                    </button>
                    {inPk.length > 0 && <span className="only inpk">in {inPk.map((x) => x.name).join(', ')}</span>}
                  </td>
                  <td className="mono">{mine.length ? mine.map((i) => i.number).join(', ') : targets.length ? `${targets.join(', ')} changed` : '—'}</td>
                  <td></td>
                </tr>,
                open.has(a.id) && (
                  <tr key={`${a.id}:open`} className="sub">
                    <td colSpan={5}>
                      <AddOnHome ctx={ctx} addOn={a} held={mine} under={priced?.build ?? null} withIt={priced?.res ?? null} base={priced?.build ? p.byBuild.get(priced.build.id) ?? null : ctx.asEntered.res} canEdit={canEdit} itemLine={itemLine} />
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>

      {editing && <BuildEditor ctx={ctx} build={editing} word={buildWord} onClose={() => setEditing(null)} />}
      {addingOn && <AddOnEditor ctx={ctx} word={addOnWord} onClose={() => setAddingOn(false)} />}
      {picking && <AppliesPicker ctx={ctx} table={picking.table} row={packages.rows.find((x) => x.id === picking.row.id) ?? picking.row} onClose={() => setPicking(null)} />}
    </>
  );
}

/** An open add-on row: its items, then its own controls (a swap's Make it an alternate, an alternate's gaps: Keep or Leave out, remove). */
function AddOnHome({ ctx, addOn: a, held, under, withIt, base, canEdit, itemLine }: { ctx: JobCtx; addOn: Row; held: Row[]; under: Row | null; withIt: Resolution | null; base: Resolution | null; canEdit: boolean; itemLine: (i: Row, res: Resolution | null) => JSX.Element }) {
  const { data, jobId } = ctx;
  const lib = ctx.r.lib!;
  const swap = a.recipe_swap as { catalog_item_id?: string; inputs?: Record<string, Scalar> } | null;
  const swapCatalog = swap?.catalog_item_id;
  const targets = ids(a.target_items).map((id) => data.items.find((i) => i.id === id)).filter((x): x is Row => !!x);
  const gaps = base && withIt ? alternateGaps(base, withIt) : [];
  return (
    <div className="subl">
      {held.map((i) => itemLine(i, withIt))}
      {held.length === 0 && targets.length === 0 && <div className="ah"><span className="w">It holds no item yet. An item is put in it on the item panel (Priced with).</span></div>}
      {targets.length > 0 && (
        <div className="ah">
          <span>
            Changes {targets.map((i) => i.number).join(', ')}
            {swapCatalog && (
              <span className="w">
                {' '}
                → {lib.catalog[swapCatalog]?.name ?? '…'}
                {Object.entries((swap?.inputs ?? {}) as Record<string, Scalar>)
                  .map(([k, v]) => ` · ${lib.catalog[swapCatalog]?.inputs.find((i) => i.key === k)?.label ?? k} ${String(v ?? '')}`)
                  .join('')}
              </span>
            )}
          </span>
          {swapCatalog && canEdit && (
            <Btn
              small
              onClick={async () => {
                const cat = lib.catalog[swapCatalog]?.name ?? 'the other item';
                if (!window.confirm(`Make "${a.name}" an add-on of alternates? Each item it changes gets its own row as ${cat} (L-02A for L-02), with the sizes both share and none of the other item's typed prices: type ${cat}'s own on its item panel. The base items stay exactly as they are.`)) return;
                const made = await swapToAlternates({ jobId, lib, addOn: a, jobItems: data.items });
                toast(made.length ? `Made ${made.map((m) => m.number + (m.blank.length ? ` (type its ${m.blank.map((k) => lib.catalog[swapCatalog]?.inputs.find((i) => i.key === k)?.label ?? k).join(', ')})` : '')).join(', ')}.` : 'None of the items it changed is on the job any more.');
              }}
            >
              Make it an alternate
            </Btn>
          )}
        </div>
      )}
      {gaps.map((g) => {
        const alt = data.items.find((i) => i.id === g.alternate_id);
        return (
          <div key={`${g.alternate_id}:${g.child.id}`} className="ah gap">
            <span className="warn">
              {g.replaced_number} has {g.child.number} {g.child.name}, which {g.alternate_number} does not make{under ? ` (under ${under.name})` : ''}.
            </span>
            {canEdit && alt && (
              <span className="mini">
                <Btn small onClick={() => void keepChild(jobId, g, `${g.alternate_number}.${(withIt?.items.filter((i) => i.parent_item_id === g.alternate_id).length ?? 0) + 1}`, 0)}>
                  Keep under {g.alternate_number}
                </Btn>
                <Btn small onClick={() => void leaveChildOut(alt, g)}>
                  Leave out
                </Btn>
              </span>
            )}
          </div>
        );
      })}
      {canEdit && (
        <div className="ah">
          <span className="w">{under ? `Prices shown under ${under.name}, with this add-on on.` : ''}</span>
          <Btn
            small
            onClick={() =>
              held.length > 0
                ? toast(`${a.name} still holds ${held.map((i) => i.number).join(', ')}. Put each back in the base (its item panel, Priced with) or remove it, then remove the add-on.`)
                : window.confirm(`Remove the add-on "${a.name}"?`) && void deleteRow('add_on', a.id).catch(sayError)
            }
          >
            Remove add-on
          </Btn>
        </div>
      )}
    </div>
  );
}

/** The picker behind "Applies to · rolls in": an add-on's builds; a package's add-ons and its builds. Empty builds = every build. */
function AppliesPicker({ ctx, table, row, onClose }: { ctx: JobCtx; table: 'add_on' | 'package'; row: Row; onClose: () => void }) {
  const { data } = ctx;
  const [builds, setBuilds] = useState<string[]>(ids(row.applies_to));
  const [ons, setOns] = useState<string[]>(ids(row.add_on_ids));
  const [busy, setBusy] = useState(false);
  const flip = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const save = async () => {
    setBusy(true);
    try {
      // every build ticked is the same as none: all builds, and a build added later joins it
      const applies = builds.length === data.builds.length ? [] : data.builds.filter((b) => builds.includes(b.id)).map((b) => b.id);
      await updateRow(table, row.id, table === 'package' ? { applies_to: applies, add_on_ids: data.addOns.filter((a) => ons.includes(a.id)).map((a) => a.id) } : { applies_to: applies });
      onClose();
    } catch (e) {
      sayError(e);
      setBusy(false);
    }
  };
  const all = builds.length === 0 || builds.length === data.builds.length;
  return (
    <Modal title={`${row.name} · ${table === 'package' ? 'what it rolls in' : 'which builds it applies to'}`} onClose={onClose}>
      {table === 'package' && (
        <>
          <div className="lb">
            <span>The add-ons it rolls into one bid</span>
          </div>
          {data.addOns.length === 0 && <Empty>No add-ons on this job yet.</Empty>}
          {data.addOns.map((a) => (
            <Ln key={a.id} left={<label className="check"><input type="checkbox" checked={ons.includes(a.id)} onChange={() => setOns(flip(ons, a.id))} /> {a.name}</label>} />
          ))}
        </>
      )}
      <div className="lb" style={{ marginTop: table === 'package' ? 12 : 0 }}>
        <span>The builds it applies to · under one it doesn't, it has no price</span>
      </div>
      <Ln left={<label className="check"><input type="checkbox" checked={all} onChange={() => setBuilds([])} /> All builds</label>} />
      {data.builds.map((b) => (
        <Ln key={b.id} sub left={<label className="check"><input type="checkbox" checked={!all && builds.includes(b.id)} onChange={() => setBuilds(flip(all ? [] : builds, b.id))} /> {b.name} only</label>} />
      ))}
      <div className="row mid" style={{ gap: 6, marginTop: 12 }}>
        <Btn primary disabled={busy} onClick={() => void save()}>
          Save
        </Btn>
        {table === 'package' && (
          <Btn
            disabled={busy}
            onClick={async () => {
              if (!window.confirm(`Remove the package "${row.name}"? Its add-ons stay.`)) return;
              await deleteRow('package', row.id).catch(sayError);
              onClose();
            }}
          >
            Remove package
          </Btn>
        )}
      </div>
    </Modal>
  );
}

// ---------- the scope rows ----------

/** The scope rows with their dot grid and the suggestions on them: the Scope step's lower table, and the left column of Review pass 1. */
export function ScopePanel({ ctx }: { ctx: JobCtx }) {
  const { data, jobId } = ctx;
  const s = useSession();
  const lib = ctx.r.lib!;
  const { oppId, scope, sources, waiting, here, sourceTitle } = useScope(ctx);
  const users = useRows('user');
  const packagesAll = useRows('package', { eq: { job_id: jobId }, order: ['sort', 'created_at'] });
  // a retired package counts nowhere (2026-10-29): nothing is deleted, Restore (through the connector) brings it back
  const packages = { ...packagesAll, rows: packagesAll.rows.filter((p) => !p.retired_at) };
  const allItems = useRows('job_item', { in: { id: [...new Set(scope.rows.flatMap((x) => scopeItemIds(x)))] } });
  const p = usePricings(ctx);
  const [linking, setLinking] = useState<Row | null>(null);
  const [adding, setAdding] = useState(false);
  const [busyAll, setBusyAll] = useState(false);
  const canEdit = s.canEdit('screen.pass1') || s.canEdit('screen.jobs');
  const canDecide = s.canEdit('screen.jobs') && !ctx.locked;

  const rows = scope.rows.filter(here).sort((a, b) => Number(a.outcome !== 'pending') - Number(b.outcome !== 'pending'));
  const elsewhere = scope.rows.filter((x) => !here(x));
  const res = ctx.asEntered.res!;
  // an item by id: a stored row of this job (add-on items too), one the engine resolved (a child), or on the opportunity's other job
  const rowOf = (id: unknown) => data.items.find((i) => i.id === id) ?? null;
  const numberOf = (id: unknown) => rowOf(id)?.number ?? p.resolved.get(String(id))?.number ?? allItems.rows.find((i) => i.id === id)?.number ?? null;
  /** The stored top-level row an item id stands for: itself, or the parent of a child. */
  const topOf = (id: string): Row | null => {
    let r = rowOf(id);
    if (!r) {
      const parent = p.resolved.get(id)?.parent_item_id;
      r = parent ? rowOf(parent) : null;
    }
    while (r?.parent_item_id) r = rowOf(r.parent_item_id);
    return r;
  };

  const onRow = (rowId: unknown) => waiting.filter((x) => x.target_id === rowId);
  const additions = waiting.filter((x) => x.kind === 'scope_add');
  const currentOf = (x: Row): string | null => {
    if (x.kind === 'scope_add') {
      const want = show(x.new_value).trim().toLowerCase();
      return scope.rows.find((row) => String(row.request_text ?? '').trim().toLowerCase() === want) ? 'a row with those words is already here' : null;
    }
    const row = scope.rows.find((row) => row.id === x.target_id);
    if (!row) return '(gone)';
    return x.kind === 'scope_reword' ? String(row.request_text ?? '') : pointsAt(row, numberOf);
  };
  const stale = (x: Row) => (x.kind === 'scope_add' ? currentOf(x) !== null : show(currentOf(x)) !== show(x.old_value));

  const approveAll = async () => {
    const list = waiting.filter((x) => !stale(x));
    if (list.length === 0) return;
    if (!window.confirm(`Approve ${list.length} scope ${list.length === 1 ? 'suggestion' : 'suggestions'} as shown?`)) return;
    setBusyAll(true);
    let done = 0;
    try {
      for (const x of list) {
        await approveSuggestion(x, x.new_value, null);
        done++;
      }
      toast(`Approved ${done}.`);
    } catch (e) {
      toast(`Approved ${done}; then ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusyAll(false);
    }
  };

  const nItem = rows.filter((x) => x.outcome === 'item').length;
  const nDis = rows.filter((x) => x.outcome === 'dismissed').length;
  const nJob = rows.filter((x) => x.outcome === 'job').length;
  const nPending = rows.filter((x) => x.outcome === 'pending').length;
  const builds = data.builds;
  const pks = packages.rows;
  const ons = data.addOns;
  const nDots = builds.length + pks.length + ons.length;
  const span = 3 + nDots;
  const top = res.items.filter((i) => i.stored);
  const dismiss = (x: Row) => {
    const reason = window.prompt('Dismiss this scope row. Why?');
    if (reason && reason.trim()) void updateRow('scope_row', x.id, { outcome: 'dismissed', dismiss_reason: reason.trim(), by_user_id: s.user?.id ?? null }).catch(sayError);
  };

  return (
    <>
      <div className="sbar topline">
        <h4>Scope rows</h4>
        <span className="tally">
          <span>
            <b>{nItem}</b> {nItem === 1 ? 'has' : 'have'} an item
          </span>
          <span>
            <b>{nDis}</b> dismissed with a reason
          </span>
          <span>
            <b>{nJob}</b> {nJob === 1 ? 'is a' : 'are'} job {nJob === 1 ? 'standard' : 'standards'}
          </span>
          {nPending > 0 && (
            <span className="pend">
              <b>{nPending}</b> without an outcome
            </span>
          )}
          {waiting.length > 0 && (
            <span>
              <b>{waiting.length}</b> suggested
            </span>
          )}
        </span>
        <span className="sp" />
        {canDecide && waiting.length > 1 && (
          <Btn small primary disabled={busyAll || waiting.every(stale)} onClick={() => void approveAll()}>
            Approve all {waiting.filter((x) => !stale(x)).length}
          </Btn>
        )}
        {canEdit && (
          <Btn small onClick={() => setAdding(true)}>
            + scope row
          </Btn>
        )}
      </div>
      {sources.rows.some((x) => !x.extracted) && (
        <div className="warn sw-note">
          Not yet extracted: {sources.rows.filter((x) => !x.extracted).map((x) => x.title || x.kind).join(', ')}. Scope from {sources.rows.filter((x) => !x.extracted).length === 1 ? 'this source' : 'these sources'} may be missing below.
        </div>
      )}
      <Wide>
        <table className="scope" style={{ minWidth: 92 + 232 + 26 * nDots + 240 }}>
          <colgroup>
            <col />
            <col style={{ width: 92 }} />
            <col style={{ width: 232 }} />
            {Array.from({ length: nDots }, (_, k) => (
              <col key={k} style={{ width: 26 }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="key" colSpan={span}>
                <div className="ky">
                  <span>
                    <span className="dot" /> in that build's base
                  </span>
                  <span>
                    <span className="dot ad" /> in that add-on
                  </span>
                  <span>
                    <span className="dot pk" /> rolled into that package
                  </span>
                  <span>
                    <span className="dot no" /> that build doesn't carry it
                  </span>
                  <span>
                    <span className="dot tg" /> faded: tagged to land there
                  </span>
                </div>
              </th>
            </tr>
            <tr>
              <th></th>
              <th></th>
              <th></th>
              {builds.length > 0 && (
                <th className="grp" colSpan={builds.length}>
                  Builds
                </th>
              )}
              {pks.length > 0 && (
                <th className="grp" colSpan={pks.length}>
                  Packages
                </th>
              )}
              {ons.length > 0 && (
                <th className="grp" colSpan={ons.length}>
                  Add-ons
                </th>
              )}
            </tr>
            <tr>
              <th>Scope row, in the client's words</th>
              <th>Source</th>
              <th>Where it sits</th>
              {builds.map((b) => (
                <th key={b.id} className="rot b" title={b.name}>
                  <span>{shortName(b.name)}</span>
                </th>
              ))}
              {pks.map((x) => (
                <th key={x.id} className="rot b" title={x.name}>
                  <span>{x.name}</span>
                </th>
              ))}
              {ons.map((a) => (
                <th key={a.id} className="rot" title={a.name}>
                  <span>{shortName(a.name)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && additions.length === 0 && (
              <tr>
                <td colSpan={span}>
                  <Empty>{sources.rows.length === 0 ? 'No sources are attached to this opportunity yet, so no scope has been extracted.' : 'No scope rows yet.'}</Empty>
                </td>
              </tr>
            )}
            {rows.map((x) => {
              const itemIds = scopeItemIds(x);
              const tops = [...new Map(itemIds.map((id) => topOf(id)).filter((r): r is Row => !!r).map((r) => [r.id, r])).values()];
              const addOnId = x.outcome === 'item' ? (tops[0]?.add_on_id as string | null) ?? null : null;
              const addOn = addOnId ? ons.find((a) => a.id === addOnId) ?? null : null;
              const onJob = tops.length > 0;
              const isBase = x.outcome === 'item' && onJob && !addOnId;
              const unv = tops.some((i) => i.unverified_since || (i.is_custom && p.resolved.get(i.id)?.unverified.any));
              const prov = tops.some((i) => /provisional/i.test(String(i.note ?? '')));
              const mine = onRow(x.id);
              // where the row is tagged to land (2026-10-23): a hollow dot, until its items say where it sits
              const tagged = new Set(ids(x.tags));
              return [
                <tr key={x.id} className={x.outcome === 'dismissed' ? 'dis' : x.outcome === 'pending' ? 'pend' : ''}>
                  <td className="w">
                    {x.request_text}
                    {x.client_words && <span className="cw"> · “{x.client_words}”</span>}
                  </td>
                  <td className="src">{sourceTitle(x.source_id)}</td>
                  <td className="out">
                    {x.outcome === 'item' ? (
                      <span className="oc">
                        {itemIds.length === 0 ? (
                          <span>no item</span>
                        ) : (
                          <span className="no">
                            {itemIds.map((id, k) => {
                              const r = topOf(id);
                              return (
                                <span key={id}>
                                  {k > 0 ? ', ' : ''}
                                  {r ? (
                                    <button type="button" className="lnk" title={`Open ${numberOf(id)}`} onClick={() => ctx.go({ tab: 'item', item: id, back: 'scope' })}>
                                      {numberOf(id) ?? '?'}
                                    </button>
                                  ) : (
                                    <span title="On this opportunity's other job">{numberOf(id) ?? '?'}</span>
                                  )}
                                </span>
                              );
                            })}
                          </span>
                        )}
                        <span className="wh" title={addOn ? `${addOn.name} add-on` : undefined}>· {!onJob ? 'other job' : addOn ? `${shortName(addOn.name)} add-on` : 'Base'}</span>
                        {canEdit && <button type="button" className="chevb" aria-label="Change where it sits" title="Pick again: one item, several, or the whole job" onClick={() => setLinking(x)}><span className="chev" /></button>}
                      </span>
                    ) : x.outcome === 'job' ? (
                      <span className="oc">
                        <span>Job std{jobStandardFor(`${x.request_text ?? ''} ${x.client_words ?? ''}`, res, lib) ? ` · ${jobStandardFor(`${x.request_text ?? ''} ${x.client_words ?? ''}`, res, lib)}` : ''}</span>
                        {canEdit && <button type="button" className="chevb" aria-label="Change where it sits" onClick={() => setLinking(x)}><span className="chev" /></button>}
                      </span>
                    ) : x.outcome === 'dismissed' ? (
                      <span className="oc dis">
                        <span>Dismissed · {x.dismiss_reason || 'no reason given'}</span>
                        {canEdit && (
                          <button type="button" className="chevb" aria-label="Bring the row back" title="Undo the dismissal" onClick={() => window.confirm('Bring this row back with no outcome?') && void updateRow('scope_row', x.id, { outcome: 'pending', dismiss_reason: null }).catch(sayError)}>
                            <span className="chev" />
                          </button>
                        )}
                      </span>
                    ) : canEdit ? (
                      <span className="mini">
                        {tagged.size > 0 && <span className="tag off" title="Where the row is meant to land, until its item is on the job">→ {[...tagged].map((id) => String(builds.find((b) => b.id === id)?.name ?? pks.find((k) => k.id === id)?.name ?? ons.find((a) => a.id === id)?.name ?? '?')).join(', ')} · tagged</span>}
                        <Btn small onClick={() => setLinking(x)}>
                          Item?
                        </Btn>
                        <Btn small onClick={() => dismiss(x)}>
                          Dismiss
                        </Btn>
                      </span>
                    ) : (
                      <span className="tag fix">{tagged.size > 0 ? 'tagged, no item yet' : 'no outcome'}</span>
                    )}
                    {x.unverified_since ? <> <AiChangedTag table="scope_row" row={x as any} users={users.rows as any[]} /></> : unv ? <> <span className="tag unv">unverified</span></> : null}
                    {prov && <> <span className="tag fix">provisional</span></>}
                  </td>
                  {builds.map((b) => {
                    let dot: string | null = null;
                    if (isBase) dot = tops.every((i) => carries(p.byBuild.get(b.id) ?? null, i.id)) ? 'dot' : 'dot no';
                    else if (addOn) dot = p.appliesTo(addOn, b) ? 'dot ad' : 'dot no';
                    else if (tagged.has(b.id)) dot = 'dot tg';
                    // a tagged build is the base dot, a ghost of itself until an item lands there
                    return (
                      <td key={b.id} className="c">
                        {dot && <span className={dot} title={`${b.name}: ${dot === 'dot' ? 'in its base' : dot === 'dot ad' ? `in ${addOn?.name}` : dot === 'dot tg' ? 'tagged to land here' : "doesn't carry it"}`} />}
                      </td>
                    );
                  })}
                  {pks.map((pk) => (
                    <td key={pk.id} className="c pkc">
                      {addOn && ids(pk.add_on_ids).includes(addOn.id) ? <span className="dot pk" title={`Rolled into ${pk.name}`} /> : tagged.has(pk.id) ? <span className="dot pk tg" title={`Tagged to land in ${pk.name}`} /> : null}
                    </td>
                  ))}
                  {ons.map((a) => (
                    <td key={a.id} className="c">
                      {addOn?.id === a.id ? <span className="dot ad" title={`In ${a.name}`} /> : tagged.has(a.id) ? <span className="dot ad tg" title={`Tagged to land in ${a.name}`} /> : null}
                    </td>
                  ))}
                </tr>,
                ...mine.map((sg) => (
                  <tr key={sg.id} className="sgrow">
                    <td colSpan={span}>
                      <ScopeSuggestion s={sg} users={users.rows} canDecide={canDecide} current={currentOf(sg)} stale={stale(sg)} numberOf={numberOf} />
                    </td>
                  </tr>
                )),
              ];
            })}
            {additions.length > 0 && (
              <tr className="kind">
                <td colSpan={span}>
                  Suggested new {additions.length === 1 ? 'row' : 'rows'} · {additions.length}
                </td>
              </tr>
            )}
            {additions.map((sg) => (
              <tr key={sg.id} className="sgrow">
                <td colSpan={span}>
                  <ScopeSuggestion s={sg} users={users.rows} canDecide={canDecide} current={currentOf(sg)} stale={stale(sg)} numberOf={numberOf} sourceTitle={sourceTitle} />
                </td>
              </tr>
            ))}
            {elsewhere.length > 0 && (
              <tr className="more">
                <td colSpan={span}>
                  + {elsewhere.length} more scope {elsewhere.length === 1 ? 'row belongs' : 'rows belong'} to this opportunity's other job ({[...new Set(allItems.rows.filter((i) => i.job_id !== jobId).map((i) => i.number))].join(', ')})
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Wide>
      {!canDecide && waiting.length > 0 && <p className="only sw-note">Only the owner approves or sends back{ctx.locked ? '; this job is locked' : ''}.</p>}

      {linking && <LinkScope row={linking} items={[...top, ...data.items.filter((i) => i.add_on_id && !i.parent_item_id).map((i) => p.resolved.get(i.id)).filter((i): i is ResolvedItem => !!i && !top.some((t) => t.id === i.id))]} addOnName={(id) => ons.find((a) => a.id === id)?.name ?? null} onClose={() => setLinking(null)} />}
      {adding && <AddScope oppId={oppId} sources={sources.rows} count={scope.rows.length} onClose={() => setAdding(false)} />}
    </>
  );
}

// ---------- the View and the mark audit ----------

function ViewPane({ ctx }: { ctx: JobCtx }) {
  const { data } = ctx;
  const lib = ctx.r.lib!;
  const s = useSession();
  const canEdit = (s.canEdit('screen.pass1') || s.canEdit('screen.jobs')) && !ctx.locked;
  const views = data.views;
  const view = views.find((v) => v.id === ctx.viewId) ?? views[0] ?? null;
  const file = useRows('file', { eq: { id: view?.file_id }, enabled: !!view?.file_id }).rows[0];
  const url = useSignedUrl(file?.storage_path);
  const [pointing, setPointing] = useState<Row | null>(null);
  const viewName = (v: Row, k: number) => String(v.name || v.kind || `View ${k + 1}`);
  const current = new Set(views.map((v) => v.id));
  const marks = data.marks.filter((m) => m.view_id === view?.id);
  const itemOf = (id: unknown) => data.items.find((i) => i.id === id) ?? null;
  const top = data.items.filter((i) => !i.parent_item_id);
  const threshold = (i: Row): string => (i.catalog_item_id ? lib.catalog[i.catalog_item_id]?.mark_threshold ?? 'top_tier' : 'top_tier');
  const orphans = data.marks.filter((m) => current.has(m.view_id) && !itemOf(m.job_item_id));
  const marked = (id: unknown) => data.marks.some((m) => current.has(m.view_id) && m.job_item_id === id);
  // an alternate stands where the item it replaces stands: that item's mark is its mark
  const unmarked = views.length ? top.filter((i) => threshold(i) === 'top_tier' && !marked(i.id) && !(i.replaces_item_id && marked(i.replaces_item_id))) : [];
  const alternates = top.filter((i) => i.replaces_item_id && marked(i.replaces_item_id)).map((i) => i.number);
  const byDesign = top.filter((i) => threshold(i) === 'no_mark').map((i) => i.number);

  return (
    <div className="view">
      <div className="vhead">
        <span className="lbl">View</span>
        <div className="vpick">
          {views.map((v, k) => (
            <button key={v.id} type="button" className={v.id === view?.id ? 'on' : ''} onClick={() => ctx.go({ view: k === 0 ? null : v.id })}>
              {viewName(v, k)}
            </button>
          ))}
        </div>
        <span className="sp" />
      </div>
      <div className="draw">
        {!view ? (
          <div className="roomnote">
            <b>No View on this job yet</b>
            <span>A View is any picture of the job: a mockup, a rendering, a line drawing. Add one under Files.</span>
          </div>
        ) : (
          <div className="pic view">
            {url ? <img src={url} alt={viewName(view, views.indexOf(view))} /> : <div className="blank">{view.file_id ? 'Loading the picture…' : 'This View has no picture yet.'}</div>}
            {url &&
              marks.map((m) => {
                const it = itemOf(m.job_item_id);
                return (
                  <span
                    key={m.id}
                    className={`mk${it?.add_on_id ? ' ad' : ''}${!it ? ' orphan' : ''}${it?.unverified_since ? ' un' : ''}`}
                    style={{ left: `${m.x_pct}%`, top: `${m.y_pct}%` }}
                    role="button"
                    tabIndex={0}
                    title={it ? `${it.number} · ${it.name}${it.add_on_id ? ` · in ${data.addOns.find((a) => a.id === it.add_on_id)?.name ?? 'an add-on'}` : ''}` : 'A mark with no item behind it'}
                    onClick={() => it && ctx.go({ tab: 'item', item: it.id, back: 'scope' })}
                    onKeyDown={(e) => e.key === 'Enter' && it && ctx.go({ tab: 'item', item: it.id, back: 'scope' })}
                  >
                    {it?.number ?? '?'}
                  </span>
                );
              })}
          </div>
        )}
      </div>
      {view && <div className="dnote">Each mark is an item number. Dashed marks are items in an add-on{orphans.length ? '; a red dashed mark has no item behind it' : ''}.</div>}
      <div className="audit">
        <h4>Mark audit</h4>
        {orphans.map((m) => {
          const v = views.find((x) => x.id === m.view_id)!;
          return (
            <div key={m.id} className="arow">
              <div className="v">
                Mark with no item · {viewName(v, views.indexOf(v))}
                <small>
                  at {Math.round(Number(m.x_pct))}% across, {Math.round(Number(m.y_pct))}% down
                </small>
              </div>
              {canEdit && (
                <div className="mini">
                  <Btn small onClick={() => window.confirm('Remove this mark?') && void deleteRow('mark', m.id).catch(sayError)}>
                    Remove mark
                  </Btn>
                  <Btn small onClick={() => setPointing(m)}>
                    Point at an item
                  </Btn>
                </div>
              )}
            </div>
          );
        })}
        {unmarked.map((i) => (
          <div key={i.id} className="arow">
            <div className="v">
              {i.number} · {i.name} has no mark on a View
              <small>{i.add_on_id ? `in ${data.addOns.find((a) => a.id === i.add_on_id)?.name ?? 'an add-on'} · ` : ''}its mark is placed on the item panel</small>
            </div>
            <div className="mini">
              <Btn small read onClick={() => ctx.go({ tab: 'item', item: i.id, back: 'scope' })}>
                Open the item
              </Btn>
            </div>
          </div>
        ))}
        {!views.length && (
          <div className="arow">
            <div className="v">
              This job has no View yet
              <small>so no item can carry a mark</small>
            </div>
          </div>
        )}
        {views.length > 0 && unmarked.length === 0 && (
          <div className="arow ok">
            <div className="v">
              Every item with a wall has a mark on its View
              <small>{[...byDesign, 'the job BOM', 'the job route'].join(', ').replace(/, ([^,]*)$/, ' and $1')} have none by design{alternates.length ? `; ${alternates.join(', ')} stand on the mark of the item each replaces` : ''}</small>
            </div>
          </div>
        )}
        {views.length > 0 && orphans.length === 0 && (
          <div className="arow ok">
            <div className="v">Every mark has an item behind it</div>
          </div>
        )}
      </div>
      {pointing && <PointMark ctx={ctx} mark={pointing} onClose={() => setPointing(null)} />}
    </div>
  );
}

/** Point at an item: a mark with no item gets one that has no mark on that View yet. */
function PointMark({ ctx, mark, onClose }: { ctx: JobCtx; mark: Row; onClose: () => void }) {
  const { data } = ctx;
  const free = data.items.filter((i) => !i.parent_item_id && !data.marks.some((m) => m.view_id === mark.view_id && m.job_item_id === i.id));
  return (
    <Modal title="Point this mark at an item" onClose={onClose}>
      {free.length === 0 && <Empty>Every item already has a mark on this View.</Empty>}
      {free.map((i) => (
        <Ln
          key={i.id}
          onClick={async () => {
            try {
              await updateRow('mark', mark.id, { job_item_id: i.id });
              onClose();
            } catch (e) {
              sayError(e);
            }
          }}
          left={`${i.number} · ${i.name}`}
          right={i.add_on_id ? <span className="tag addon">add-on</span> : undefined}
        />
      ))}
    </Modal>
  );
}

/** One suggestion under its scope row: what it does in words, the box, the reason and source, who and when, the two buttons. */
function ScopeSuggestion({ s, users, canDecide, current, stale, numberOf, sourceTitle }: { s: Row; users: Row[]; canDecide: boolean; current: string | null; stale: boolean; numberOf: (id: unknown) => string | null; sourceTitle?: (id: unknown) => string }) {
  const [box, setBox] = useState<string | undefined>(undefined);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const p = (s.payload ?? {}) as Record<string, unknown>;
  const who = users.find((u) => u.id === s.suggested_by)?.name ?? '—';
  const value = box ?? show(s.new_value);
  // scope_source, scope_tag and scope_outcome (2026-10-23) carry no words to edit but a dismissal's reason
  const hasBox = s.kind !== 'scope_link' && s.kind !== 'scope_source' && s.kind !== 'scope_tag' && !(s.kind === 'scope_outcome' && p.outcome !== 'dismissed');
  const becameWords = p.whole_job ? 'the whole job' : Array.isArray(p.item_numbers) ? (p.item_numbers as unknown[]).join(', ') : String(p.item_number ?? show(s.new_value));
  const kindWords =
    s.kind === 'scope_reword' ? 'New words' : s.kind === 'scope_link' ? (p.whole_job ? 'Applies to the whole job' : `Became ${becameWords}`) : s.kind === 'scope_dismiss' ? 'Dismiss with the reason'
      : s.kind === 'scope_source' ? `Source → ${String(p.source_title ?? '(none)')}`
      : s.kind === 'scope_tag' ? `Tagged to ${Array.isArray(p.tag_words) && (p.tag_words as unknown[]).length ? (p.tag_words as unknown[]).join(', ') : 'nothing'}`
      : s.kind === 'scope_outcome' ? (p.outcome === 'item' ? `Became ${becameWords}` : p.outcome === 'job' ? 'Applies to the whole job' : p.outcome === 'dismissed' ? 'Dismiss with the reason' : 'No outcome (pending)')
      : p.dismiss_reason ? 'Add this line, dismissed' : p.whole_job ? 'Add this line, for the whole job' : p.item_numbers || p.item_number ? `Add this line at ${becameWords}` : 'Add this line, no outcome yet';
  const was = s.kind === 'scope_reword' ? null : s.kind === 'scope_add' ? null : `was ${show(s.old_value)}`;

  const approve = async () => {
    setBusy(true);
    try {
      await approveSuggestion(s, boxValue(s, box), note.trim() || null);
      toast(approvedWords(s));
    } catch (e) {
      sayError(e);
    } finally {
      setBusy(false);
    }
  };
  const sendBack = async () => {
    setBusy(true);
    try {
      await sendBackSuggestion(s, note);
      toast('Sent back.');
    } catch (e) {
      sayError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="scope-suggest">
      <div className="row mid" style={{ gap: 8, flexWrap: 'wrap' }}>
        <Tag kind="un">suggested</Tag>
        <small>
          <b>{kindWords}</b>
          {was ? ` · ${was}` : ''}
          {s.kind === 'scope_add' && p.source_id && sourceTitle ? ` · from ${sourceTitle(p.source_id)}` : ''}
        </small>
      </div>
      {hasBox ? (
        <textarea className="cell scope-box" rows={2} value={value} disabled={!canDecide || stale} onChange={(e) => setBox(e.target.value)} aria-label={s.kind === 'scope_dismiss' ? 'The reason' : 'The words'} />
      ) : null}
      {s.kind === 'scope_add' && p.client_words ? <small className="muted">“{String(p.client_words)}”</small> : null}
      {s.kind === 'scope_reword' && p.client_words ? <small className="muted">client's words: “{String(p.client_words)}”</small> : null}
      <small>
        {s.reason} · {/^https?:\/\//.test(String(s.source)) ? <a href={String(s.source)} target="_blank" rel="noreferrer">{String(s.source).replace(/^https?:\/\//, '').slice(0, 60)}</a> : String(s.source)} · {who}, {longDate(s.suggested_at)}
      </small>
      {stale && (
        <div className="warn">
          {s.kind === 'scope_add' ? current : `now ${show(current)}, not ${show(s.old_value)}`}: suggest it again
        </div>
      )}
      {canDecide ? (
        <div className="row mid" style={{ gap: 6, flexWrap: 'wrap' }}>
          <Btn small primary disabled={busy || stale} onClick={() => void approve()}>
            Approve as shown
          </Btn>
          <Btn small disabled={busy} onClick={() => void sendBack()}>
            Send back
          </Btn>
          <input className="cell grow" placeholder="note" value={note} onChange={(e) => setNote(e.target.value)} style={{ minWidth: 140 }} />
        </div>
      ) : (
        <Tag kind="un">waiting</Tag>
      )}
      {s.kind === 'scope_link' && !p.whole_job && (Array.isArray(p.job_item_ids) ? (p.job_item_ids as unknown[]) : [p.job_item_id]).some((id) => numberOf(id) === null) && !stale && (
        <small className="warn">An item it names is not on this job now.</small>
      )}
    </div>
  );
}

/** Item?: what a line became: one item, several (a line that is more than one box), or the whole job (a build, a standard, hardware across the job). */
function LinkScope({ row, items, addOnName, onClose }: { row: Row; items: ResolvedItem[]; addOnName: (id: unknown) => string | null; onClose: () => void }) {
  const { user } = useSession();
  const [picked, setPicked] = useState<string[]>(() => scopeItemIds(row).filter((id) => items.some((i) => i.id === id)));
  const [busy, setBusy] = useState(false);
  const save = async (patch: Row) => {
    setBusy(true);
    try {
      await updateRow('scope_row', row.id, { ...patch, dismiss_reason: null, by_user_id: user?.id ?? null });
      onClose();
    } catch (e) {
      sayError(e);
      setBusy(false);
    }
  };
  return (
    <Modal title={`What did “${row.request_text}” become?`} onClose={onClose}>
      <div className="row mid" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <Btn small primary={row.outcome === 'job'} disabled={busy} onClick={() => void save({ outcome: 'job', job_item_ids: [] })}>
          The whole job
        </Btn>
        <small>A build, a job standard, hardware across the job: nothing to point at.</small>
      </div>
      {items.length === 0 && <Empty>No items on this job yet. Add one above, then come back.</Empty>}
      {items.map((i) => (
        <Ln
          key={i.id}
          sub={!!i.parent_item_id}
          left={
            <label className="check" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={picked.includes(i.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, i.id] : picked.filter((x) => x !== i.id))} />
              <span>
                {i.number} · {i.name}
                {i.add_on_id ? <> <span className="tag addon">{addOnName(i.add_on_id) ?? "add-on"}</span></> : null}
              </span>
            </label>
          }
        />
      ))}
      <div className="row mid" style={{ gap: 8, marginTop: 12, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <small>{picked.length === 0 ? 'Tick the item it became, or several when one line is more than one box.' : `${picked.length} ${picked.length === 1 ? 'item' : 'items'} picked`}</small>
        <Btn primary disabled={busy || picked.length === 0} onClick={() => void save({ outcome: 'item', job_item_ids: items.filter((i) => picked.includes(i.id)).map((i) => i.id) })}>
          {picked.length > 1 ? `These ${picked.length}` : 'This one'}
        </Btn>
      </div>
    </Modal>
  );
}

/** + Scope: a line the extraction missed, typed by hand. */
function AddScope({ oppId, sources, count, onClose }: { oppId: string; sources: Row[]; count: number; onClose: () => void }) {
  const { user } = useSession();
  const [text, setText] = useState('');
  const [words, setWords] = useState('');
  const [source, setSource] = useState('');
  return (
    <Modal title="+ Scope · a piece of scope the extraction missed" onClose={onClose}>
      <div className="form">
        <label className="field wide">
          <span>What the client gets, in plain language</span>
          <input type="text" autoFocus value={text} onChange={(e) => setText(e.target.value)} />
        </label>
        <label className="field wide">
          <span>The client's own words</span>
          <input type="text" value={words} onChange={(e) => setWords(e.target.value)} />
        </label>
        <label className="field">
          <span>Source</span>
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">Added by hand</option>
            {sources.map((x) => (
              <option key={x.id} value={x.id}>
                {x.title || x.kind}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div style={{ marginTop: 12 }}>
        <Btn
          primary
          disabled={!text.trim()}
          onClick={async () => {
            await insertRow('scope_row', { opportunity_id: oppId, source_id: source || null, request_text: text.trim(), client_words: words.trim() || null, by_user_id: user?.id ?? null, sort: count + 1 });
            onClose();
          }}
        >
          Add
        </Btn>
      </div>
    </Modal>
  );
}

// ---------- a build's standards and percentages, a new add-on (carried from the Builds tab) ----------

function BuildEditor({ ctx, build, word, onClose }: { ctx: JobCtx; build: Row; word: string; onClose: () => void }) {
  const lib = ctx.r.lib!;
  const base = ctx.r.res!;
  const [name, setName] = useState<string>(build.name ?? '');
  const [over, setOver] = useState<Record<string, Scalar>>({ ...(build.global_overrides ?? {}) });
  // its own percentages (2026-10-17): a key here is this Build's figure instead of the job's
  const [pct, setPct] = useState<Record<string, string | number>>({ ...((build.pricing_overrides ?? {}) as Record<string, string | number>) });
  const defs = lib.standards.filter((d) => d.data_type !== 'material' && d.kind !== 'catalog');
  const save = async () => {
    const row = { name: name.trim() || `${word} ${ctx.data.builds.length + 1}`, global_overrides: over, pricing_overrides: pct };
    if (build.id) await updateRow('build', build.id, row);
    else await insertRow('build', { ...row, job_id: ctx.jobId, is_base: !!build.is_base, published: false, sort: ctx.data.builds.length + 1 });
    onClose();
  };
  return (
    <Modal title={build.id ? `${word} · ${build.name}` : `New ${word.toLowerCase()}`} onClose={onClose}>
      <Field label="Name">
        <input type="text" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <div className="lb" style={{ marginTop: 12 }}>
        <span>What this {word.toLowerCase()} assumes differently · tick a standard to change it</span>
      </div>
      {defs.map((d) => {
        const on = d.key in over;
        const unit = d.data_type === 'number' ? d.unit ?? null : null;
        return (
          <Ln
            key={d.key}
            left={
              <label className="check">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={(e) => {
                    const next = { ...over };
                    if (e.target.checked) next[d.key] = base.standards[d.key] ?? null;
                    else delete next[d.key];
                    setOver(next);
                  }}
                />
                {d.label}
              </label>
            }
            right={
              on ? (
                <span className="ov">
                  {d.data_type === 'catalog_item' ? (
                    <CatalogPick lib={lib} value={String(over[d.key] ?? '')} onPick={(id) => setOver({ ...over, [d.key]: id })} />
                  ) : d.data_type === 'recipe' ? (
                    <RecipePick lib={lib} value={String(over[d.key] ?? '')} onPick={(name) => setOver({ ...over, [d.key]: name })} />
                  ) : (
                    <ValueEditor type={d.data_type} unit={unit} choices={d.choices?.map((c) => c.value) ?? null} value={over[d.key] ?? null} onSave={(v) => setOver({ ...over, [d.key]: v })} unavailable={d.data_type === 'choice' ? unavailableChoices(lib, { ...base.standards, ...over }, d) : undefined} />
                  )}
                </span>
              ) : (
                <span className="lk">
                  {d.data_type === 'catalog_item' ? lib.catalog[String(base.standards[d.key] ?? '')]?.name ?? '—' : showValue(base.standards[d.key] ?? null, d.data_type, unit)}
                </span>
              )
            }
          />
        );
      })}
      <div className="lb" style={{ marginTop: 12 }}>
        <span>Its own percentages · tick one to mark this {word.toLowerCase()} up differently from the job</span>
      </div>
      {BUILD_PCTS.filter((x) => String(x.key) !== "large_purchase_markup_pct").map((p) => {
        const on = p.key in pct;
        const jobFigure = String(base.pricing[p.key] ?? 0);
        return (
          <Ln
            key={p.key}
            left={
              <label className="check">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={(e) => {
                    const next = { ...pct };
                    if (e.target.checked) next[p.key] = jobFigure;
                    else delete next[p.key];
                    setPct(next);
                  }}
                />
                {p.label}
              </label>
            }
            right={
              on ? (
                <span className="ov nowrap">
                  <Edit className="cell short right" value={String(pct[p.key] ?? '')} parse={(x) => (/^\d*\.?\d+$/.test(x.trim()) ? x.trim() : undefined)} onSave={(v) => setPct({ ...pct, [p.key]: v })} />% <small>this {word.toLowerCase()} (job {jobFigure}%)</small>
                </span>
              ) : (
                <span className="lk">{jobFigure}% · the job's</span>
              )
            }
          />
        );
      })}
      <div className="row mid" style={{ marginTop: 12, gap: 6 }}>
        <Btn primary onClick={() => void save()}>
          Save
        </Btn>
        {build.id && !build.is_base && (
          <Btn
            onClick={async () => {
              if (!window.confirm(`Remove "${build.name}"?`)) return;
              await deleteRow('build', build.id);
              onClose();
            }}
          >
            Remove
          </Btn>
        )}
      </div>
    </Modal>
  );
}

function AddOnEditor({ ctx, word, onClose }: { ctx: JobCtx; word: string; onClose: () => void }) {
  const lib = ctx.r.lib!;
  const res = ctx.r.res!;
  const stored = res.items.filter((i) => i.stored && !i.is_custom);
  // two kinds (2026-10-17): it changes items the job already prices (a swap), or it adds items the base job leaves out
  const [kind, setKind] = useState<'changes' | 'adds'>('changes');
  const [name, setName] = useState('');
  // every top-level item from the rows, not the resolution: an item already in an add-on is not in the base price
  const topLevel = ctx.data.items.filter((i) => !i.parent_item_id);
  const free = topLevel.filter((i) => !i.add_on_id);
  const heldElsewhere = topLevel.filter((i) => i.add_on_id);
  const addOnName = (id: unknown) => ctx.data.addOns.find((a) => a.id === id)?.name ?? 'another add-on';
  const [adds, setAdds] = useState<string[]>(ctx.selected.filter((id) => free.some((i) => i.id === id)));
  const taken = ctx.data.addOns.some((a) => String(a.name ?? '').trim().toLowerCase() === name.trim().toLowerCase());
  const [targets, setTargets] = useState<string[]>(ctx.selected.filter((id) => stored.some((i) => i.id === id)));
  const [key, setKey] = useState('');
  const [value, setValue] = useState<Scalar>(null);
  const [recipeId, setRecipeId] = useState('');
  // the item as another catalog item (door bases as drawer bases): the inputs both share carry over, the rest are typed here
  const [swapCat, setSwapCat] = useState('');
  const [swapInputs, setSwapInputs] = useState<Record<string, Scalar>>({});
  const first = stored.find((i) => i.id === targets[0]);
  const cat = first?.catalog_item_id ? lib.catalog[first.catalog_item_id] : undefined;
  const sameKind = targets.every((id) => stored.find((i) => i.id === id)?.catalog_item_id === first?.catalog_item_id);
  const input = cat?.inputs.find((i) => i.key === key);
  const alternatives = cat?.recipes.filter((x) => x.id !== first?.recipe_id) ?? [];
  const swapTo = swapCat ? lib.catalog[swapCat] : undefined;
  // what the new item needs that the old one cannot hand it: its own inputs, and its own typed price (a Cabinotch price is the other item's quote)
  const newInputs = swapTo ? swapTo.inputs.filter((i) => i.data_type !== 'text' && (!cat?.inputs.some((x) => x.key === i.key) || String(i.unit ?? '').trim() === '$')) : [];
  const ready = !!name.trim() && !taken && (kind === 'adds' ? adds.length > 0 : targets.length > 0 && (sameKind || !!swapCat) && !!(swapCat || recipeId || (key && value !== null)));

  // another catalog item for each picked item (door bases as drawer bases): an alternate of its own, priced only with the
  // add-on on, with the sizes both share and none of the other item's typed prices (Spencer, 2026-10-17)
  const saveAlternates = async () => {
    const rows = targets.map((id) => ctx.data.items.find((i) => i.id === id)).filter((x): x is Row => !!x);
    const notBase = rows.filter((i) => i.parent_item_id || i.add_on_id);
    if (notBase.length > 0) {
      toast(`${notBase.map((i) => i.number).join(', ')} ${notBase.length === 1 ? 'is' : 'are'} not an item of the base job of ${notBase.length === 1 ? 'its' : 'their'} own, so an alternate cannot stand in for ${notBase.length === 1 ? 'it' : 'them'}. Nothing was saved.`);
      return;
    }
    const row = await insertRow('add_on', {
      job_id: ctx.jobId,
      name: name.trim(),
      scope: rows.length === 1 ? 'item' : 'group',
      target_items: [],
      recipe_swap: {},
      published: false,
      sort: ctx.data.addOns.length + 1,
    });
    const made = await addAlternates({ jobId: ctx.jobId, lib, addOnId: row.id as string, targets: rows, catalogId: swapCat, typed: swapInputs, jobItems: ctx.data.items });
    const blank = [...new Set(made.flatMap((m) => m.blank))].map((k) => swapTo?.inputs.find((i) => i.key === k)?.label ?? k);
    toast(`Made ${made.map((m) => m.number).join(', ')} as ${swapTo?.name ?? 'the new item'}${blank.length ? `; ${blank.join(', ')} starts blank: type it on each one's Item tab` : ''}.`);
    onClose();
  };

  const saveAdds = async () => {
    // read the items fresh: one already moved into an add-on since this screen loaded is refused, not taken over
    const now = await fetchRows('job_item', { in: { id: adds } });
    const elsewhere = now.filter((i) => i.add_on_id || i.parent_item_id);
    if (elsewhere.length > 0) {
      toast(`${elsewhere.map((i) => i.number).join(', ')} ${elsewhere.length === 1 ? 'is' : 'are'} already in an add-on or under another item. Nothing was saved.`);
      return;
    }
    const row = await insertRow('add_on', {
      job_id: ctx.jobId,
      name: name.trim(),
      scope: 'item',
      target_items: [],
      recipe_swap: {},
      published: false,
      sort: ctx.data.addOns.length + 1,
    });
    // only items still in the base job, still top-level, move into it
    const moved = await updateRows('job_item', adds, { add_on_id: row.id }, { add_on_id: null, parent_item_id: null });
    if (moved.length === 0) {
      await deleteRow('add_on', row.id as string);
      toast('No item could be moved into the add-on, so it was not kept.');
      return;
    }
    const left = adds.filter((id) => !moved.includes(id)).map((id) => topLevel.find((i) => i.id === id)?.number);
    if (left.length > 0) toast(`${left.join(', ')} moved elsewhere meanwhile and stayed where ${left.length === 1 ? 'it was' : 'they were'}.`);
    onClose();
  };

  const save = async () => {
    if (kind === 'adds') return saveAdds();
    if (swapCat) return saveAlternates();
    await insertRow('add_on', {
      job_id: ctx.jobId,
      name: name.trim(),
      scope: targets.length === 1 ? 'item' : targets.length === stored.length ? 'job' : 'group',
      target_items: targets,
      recipe_swap: { ...(swapCat ? { catalog_item_id: swapCat, inputs: swapInputs } : recipeId ? { recipe_id: recipeId } : {}), ...(key ? { overrides: { [key]: value } } : {}) },
      published: false,
      sort: ctx.data.addOns.length + 1,
    });
    onClose();
  };

  return (
    <Modal title={`New ${word.toLowerCase()}`} onClose={onClose}>
      <div className="form">
        <Field label="Name" wide>
          <input type="text" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
      </div>
      {taken && <p className="warn" style={{ marginTop: 8 }}>This job already has an add-on named {name.trim()}.</p>}
      <div style={{ marginTop: 12 }}>
        <Seg
          items={[
            { key: 'changes', label: 'Changes items' },
            { key: 'adds', label: 'Adds items' },
          ]}
          value={kind}
          onChange={setKind}
        />
        <p style={{ margin: '6px 0 0' }}>
          <small>
            {kind === 'adds'
              ? "The items picked here are left out of the job's price until the client takes the add-on."
              : "The items stay in the job's price as they are; taking the add-on prices them as changed here."}
          </small>
        </p>
      </div>
      {kind === 'adds' ? (
        <>
          <div className="lb" style={{ marginTop: 12 }}>
            <span>Which items it adds · each leaves the base job</span>
          </div>
          {free.length === 0 ? (
            <Empty>Every item of its own is already in an add-on.</Empty>
          ) : (
            <div className="seg">
              {free.map((i) => (
                <button key={i.id} type="button" title={i.name} className={`tag${adds.includes(i.id) ? ' on' : ''}`} onClick={() => setAdds(adds.includes(i.id) ? adds.filter((x) => x !== i.id) : [...adds, i.id])}>
                  {i.number}
                </button>
              ))}
            </div>
          )}
          {[...new Set(heldElsewhere.map((i) => i.add_on_id as string))].map((id) => (
            <p key={id} style={{ margin: '8px 0 0' }}>
              <small>
                Already in {addOnName(id)}, so not offered: {heldElsewhere.filter((i) => i.add_on_id === id).map((i) => i.number).join(', ')}
              </small>
            </p>
          ))}
        </>
      ) : (
        <>
          <div className="lb" style={{ marginTop: 12 }}>
            <span>Which items it touches · one item, a group, or all of a kind</span>
          </div>
          <div className="seg">
            {stored.map((i) => (
              <button key={i.id} type="button" className={`tag${targets.includes(i.id) ? ' on' : ''}`} onClick={() => setTargets(targets.includes(i.id) ? targets.filter((x) => x !== i.id) : [...targets, i.id])}>
                {i.number}
              </button>
            ))}
          </div>
          {!sameKind && <p className="warn" style={{ marginTop: 8 }}>Pick items of one catalog item, so one switch means the same thing on each.</p>}
          {cat && sameKind && (
            <div className="form" style={{ marginTop: 12 }}>
              <Field label="Make it another catalog item" wide>
                <CatalogPick
                  lib={lib}
                  value={swapCat}
                  onPick={(id) => {
                    setSwapCat(id === cat.id ? '' : id);
                    setSwapInputs({});
                  }}
                />
              </Field>
              {swapTo && (
                <small style={{ gridColumn: '1 / -1' }}>
                  Each picked item gets an alternate of its own as {swapTo.name} ({targets.length === 1 ? `${stored.find((i) => i.id === targets[0])?.number ?? ''}A` : 'L-02A for L-02'}), priced only with the add-on on, with its own Item tab. Width, height, depth and every input both items share carry over, except a price typed for the other item, which starts blank.{newInputs.length > 0 ? ` ${swapTo.name} also needs (blank is fine: type it on the Item tab later):` : ''}
                </small>
              )}
              {newInputs.map((i) => (
                <Field key={i.key} label={i.label}>
                  <ValueEditor type={i.data_type} unit={i.unit} choices={i.choices} value={swapInputs[i.key] ?? null} onSave={(v) => setSwapInputs({ ...swapInputs, [i.key]: v })} />
                </Field>
              ))}
              {alternatives.length > 0 && !swapCat && (
                <Field label="Switch to another recipe">
                  <select value={recipeId} onChange={(e) => setRecipeId(e.target.value)}>
                    <option value="">Keep the recipe</option>
                    {alternatives.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {!swapCat && (
              <Field label="Or change an input">
                <select
                  value={key}
                  onChange={(e) => {
                    setKey(e.target.value);
                    setValue(null);
                  }}
                >
                  <option value="">Nothing</option>
                  {cat.inputs.map((i) => (
                    <option key={i.key} value={i.key}>
                      {i.label}
                    </option>
                  ))}
                </select>
              </Field>
              )}
              {input && !swapCat && (
                <Field label={`${input.label} becomes`}>
                  <ValueEditor type={input.data_type} unit={input.unit} choices={input.choices} value={value} onSave={setValue} />
                </Field>
              )}
            </div>
          )}
        </>
      )}
      <div style={{ marginTop: 12 }}>
        <Btn primary disabled={!ready} onClick={() => void save()}>
          Add
        </Btn>
      </div>
    </Modal>
  );
}
